import React, { useState, useEffect } from 'react';
import { ReviewSubmissionFormProps, ReviewSubmissionData } from '../../types/review-types';

const GUEST_RATING_CATEGORIES = [
  { key: 'cleanliness', label: 'Cleanliness', description: 'How clean was the property?' },
  { key: 'accuracy', label: 'Accuracy', description: 'How accurate was the listing description?' },
  { key: 'communication', label: 'Communication', description: 'How was the host\'s communication?' },
  { key: 'location', label: 'Location', description: 'How was the location?' },
  { key: 'value', label: 'Value', description: 'How was the value for money?' }
];

const HOST_RATING_CATEGORIES = [
  { key: 'guestCommunication', label: 'Communication', description: 'How was the guest\'s communication?' },
  { key: 'guestCleanliness', label: 'Cleanliness', description: 'How clean did the guest leave the property?' },
  { key: 'houseRulesAdherence', label: 'House Rules', description: 'How well did the guest follow house rules?' }
];

const MAX_REVIEW_LENGTH = 1000;

export const ReviewSubmissionForm: React.FC<ReviewSubmissionFormProps> = ({
  bookingId,
  reviewerType,
  revieweeId,
  listingId,
  onSubmit,
  onCancel,
  loading = false,
  error
}) => {
  const [formData, setFormData] = useState<ReviewSubmissionData>({
    overallRating: 0,
    categoryRatings: {},
    reviewText: ''
  });
  
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categories = reviewerType === 'guest' ? GUEST_RATING_CATEGORIES : HOST_RATING_CATEGORIES;

  // Initialize category ratings
  useEffect(() => {
    const initialRatings: Record<string, number> = {};
    categories.forEach(category => {
      initialRatings[category.key] = 0;
    });
    setFormData(prev => ({
      ...prev,
      categoryRatings: initialRatings
    }));
  }, [reviewerType, categories]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Validate overall rating
    if (formData.overallRating < 1 || formData.overallRating > 5) {
      errors.overallRating = 'Please provide an overall rating between 1 and 5 stars';
    }

    // Validate category ratings
    categories.forEach(category => {
      const rating = formData.categoryRatings[category.key];
      if (!rating || rating < 1 || rating > 5) {
        errors[category.key] = `Please provide a rating for ${category.label}`;
      }
    });

    // Validate review text
    if (!formData.reviewText.trim()) {
      errors.reviewText = 'Please write a review';
    } else if (formData.reviewText.length > MAX_REVIEW_LENGTH) {
      errors.reviewText = `Review must be ${MAX_REVIEW_LENGTH} characters or less`;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOverallRatingChange = (rating: number) => {
    setFormData(prev => ({
      ...prev,
      overallRating: rating
    }));
    
    // Clear validation error
    if (validationErrors.overallRating) {
      setValidationErrors(prev => {
        const { overallRating, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleCategoryRatingChange = (category: string, rating: number) => {
    setFormData(prev => ({
      ...prev,
      categoryRatings: {
        ...prev.categoryRatings,
        [category]: rating
      }
    }));
    
    // Clear validation error
    if (validationErrors[category]) {
      setValidationErrors(prev => {
        const { [category]: removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleTextChange = (text: string) => {
    setFormData(prev => ({
      ...prev,
      reviewText: text
    }));
    
    // Clear validation error
    if (validationErrors.reviewText) {
      setValidationErrors(prev => {
        const { reviewText, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (err) {
      // Error handling is done by parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStarRating = (
    currentRating: number,
    onRatingChange: (rating: number) => void,
    label: string,
    error?: string
  ) => (
    <div className="space-y-1">
      <div className="flex items-center space-x-2">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <div className="flex space-x-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => onRatingChange(star)}
              className={`w-6 h-6 ${
                star <= currentRating
                  ? 'text-yellow-400 fill-current'
                  : 'text-gray-300 hover:text-yellow-300'
              } transition-colors duration-150`}
              disabled={loading || isSubmitting}
            >
              <svg viewBox="0 0 20 20" className="w-full h-full">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </button>
          ))}
        </div>
        <span className="text-sm text-gray-500">
          {currentRating > 0 ? `${currentRating} star${currentRating !== 1 ? 's' : ''}` : 'No rating'}
        </span>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {reviewerType === 'guest' ? 'Review Your Stay' : 'Review Your Guest'}
        </h2>
        <p className="text-gray-600">
          {reviewerType === 'guest' 
            ? 'Share your experience to help future guests and improve the platform'
            : 'Share your experience to help other hosts make informed decisions'
          }
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Overall Rating */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Overall Rating</h3>
          {renderStarRating(
            formData.overallRating,
            handleOverallRatingChange,
            'How would you rate your overall experience?',
            validationErrors.overallRating
          )}
        </div>

        {/* Category Ratings */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Detailed Ratings</h3>
          <div className="space-y-4">
            {categories.map((category) => (
              <div key={category.key} className="space-y-2">
                {renderStarRating(
                  formData.categoryRatings[category.key] || 0,
                  (rating) => handleCategoryRatingChange(category.key, rating),
                  category.label,
                  validationErrors[category.key]
                )}
                <p className="text-sm text-gray-500 ml-0">{category.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Review Text */}
        <div className="space-y-2">
          <label htmlFor="reviewText" className="block text-lg font-semibold text-gray-900">
            Written Review
          </label>
          <p className="text-sm text-gray-600">
            Share details about your experience to help others
          </p>
          <textarea
            id="reviewText"
            value={formData.reviewText}
            onChange={(e) => handleTextChange(e.target.value)}
            placeholder={reviewerType === 'guest' 
              ? 'Describe your stay, what you liked, and any suggestions for improvement...'
              : 'Describe your experience with this guest, their communication, and how they treated your property...'
            }
            rows={6}
            maxLength={MAX_REVIEW_LENGTH}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              validationErrors.reviewText ? 'border-red-300' : 'border-gray-300'
            }`}
            disabled={loading || isSubmitting}
          />
          <div className="flex justify-between items-center">
            <div>
              {validationErrors.reviewText && (
                <p className="text-sm text-red-600">{validationErrors.reviewText}</p>
              )}
            </div>
            <p className="text-sm text-gray-500">
              {formData.reviewText.length}/{MAX_REVIEW_LENGTH} characters
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-150"
            disabled={loading || isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
            disabled={loading || isSubmitting}
          >
            {isSubmitting ? (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Submitting...</span>
              </div>
            ) : (
              'Submit Review'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReviewSubmissionForm;